import { useCallback, useMemo, useState } from "react";
import { getTier, previewReviewPoints } from "@shared/gamification";
import { fetchMyProfile } from "@/api/userService";
import type {
  ReviewThankYouUpsell,
  ThankYouUpsellElement,
} from "@/components/ReviewThankYou";

// The state behind the post-submit thank-you card, shared by BOTH submit surfaces
// (`ReviewsSection` on the store page and `RateStore` behind /rate/@slug).
//
// It exists as a hook rather than living twice because the two surfaces must agree on three
// things that are easy to get subtly different: what «كسبت {n} نقطة» counts, when the next-tier
// nudge is suppressed, and which upsell rows are offered. `ReviewThankYou` itself stays
// presentational, and the mutations stay in the callers (they own their own cache invalidation).
//
// The earned figure is a DELTA between two real balances, not the form's preview. Two reasons:
// the daily ceiling clamps on a UTC day boundary, so a user near the cap late in the Egyptian
// evening is credited less than the preview promised; and the ledger's per-component unique index
// means a re-submitted component pays nothing, which only the server knows about.

export type SubmittedComposition = {
  text: string;
  images: number;
  factors: number;
};

const EMPTY_COMPOSITION: SubmittedComposition = { text: "", images: 0, factors: 0 };

const ACTION_ELEMENT: Record<string, ThankYouUpsellElement> = {
  review_text: "text",
  review_photo: "photo",
  review_factors: "factors",
};

export const useReviewThankYou = () => {
  const [rating, setRating] = useState<number | null>(null);
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [composition, setComposition] = useState<SubmittedComposition>(EMPTY_COMPOSITION);
  // Balance BEFORE the submit, so the earned figure is this review's contribution and not the
  // user's lifetime total.
  const [baseline, setBaseline] = useState<number | null>(null);
  const [balance, setBalance] = useState<number | null>(null);

  // Every failure path here is silent and returns null: a points balance that cannot be read
  // hides the points line and the nudge, and the thank-you card still works. Points are status,
  // never a blocker.
  const readBalance = useCallback(async (): Promise<number | null> => {
    try {
      const profile = await fetchMyProfile();
      return typeof profile?.points === "number" ? profile.points : null;
    } catch {
      return null;
    }
  }, []);

  const captureBaseline = useCallback(async () => {
    setBaseline(await readBalance());
  }, [readBalance]);

  const open = useCallback(
    async ({
      rating: submittedRating,
      reviewId: submittedReviewId,
      composition: submitted,
    }: {
      rating: number;
      reviewId?: string | null;
      composition: SubmittedComposition;
    }) => {
      setRating(submittedRating);
      setReviewId(submittedReviewId ?? null);
      setComposition(submitted);
      setBalance(await readBalance());
    },
    [readBalance]
  );

  // Called after an upsell's mutation resolves: the satisfied row disappears because the
  // composition now includes it, and the earned figure rises by whatever the ledger actually paid.
  const applyUpsell = useCallback(
    async (patch: Partial<SubmittedComposition>) => {
      setComposition((prev) => ({ ...prev, ...patch }));
      setBalance(await readBalance());
    },
    [readBalance]
  );

  const close = useCallback(() => {
    setRating(null);
    setReviewId(null);
    setComposition(EMPTY_COMPOSITION);
    setBaseline(null);
    setBalance(null);
  }, []);

  const earnedPoints = useMemo(() => {
    if (baseline === null || balance === null) return undefined;
    return Math.max(0, balance - baseline);
  }, [baseline, balance]);

  const nextTier = useMemo(() => {
    if (balance === null) return undefined;
    const tier = getTier({ points: balance });
    // Tier 5 has nothing left to reach, so the nudge is omitted entirely rather than rendered
    // as a congratulation nobody asked for.
    if (tier.nextThreshold === null || tier.pointsToNext === null) return undefined;
    return {
      ar: getTier({ points: tier.nextThreshold }).ar,
      pointsToNext: tier.pointsToNext,
    };
  }, [balance]);

  const upsells = useMemo<ReviewThankYouUpsell[]>(() => {
    const preview = previewReviewPoints({
      text: composition.text,
      images: composition.images,
      factors: composition.factors,
    });
    return preview.components
      .filter((component) => !component.earned && ACTION_ELEMENT[component.action])
      .map((component) => ({
        element: ACTION_ELEMENT[component.action],
        points: component.points,
      }));
  }, [composition]);

  return {
    rating,
    reviewId,
    composition,
    earnedPoints,
    nextTier,
    upsells,
    captureBaseline,
    open,
    applyUpsell,
    close,
  };
};
